import {getTestProps} from '@benchling/js-test-utils';
import React from 'react';

import {AnalyticsVisualizationAxisScale as AxisScale} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {
  isVisualizationTypeParameters,
  VisualizationType,
} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import AxisRangeConfigEditor from './AxisRangeConfigEditor';

type ScatterPlotParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationScatterPlotParametersNode'
>;

type LineChartParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationLineChartParametersNode'
>;

export const Y_AXIS_INCLUDES_0_OVERRIDES_RANGE_WARNING =
  'Including 0 on the y-axis might override the set range';

export interface FormatConfigEditorProps {
  onUpdateParameters: (update: Partial<ScatterPlotParameters | LineChartParameters>) => void;
  parameters: ScatterPlotParameters | LineChartParameters;
  isAnalysisTool?: boolean;
}

/**
 * Component for format tab of ScatterPlotConfigEditor
 */
export default function FormatConfigEditor({
  onUpdateParameters,
  parameters,
  isAnalysisTool = false,
}: FormatConfigEditorProps): JSX.Element {
  let YAxisMinMaxEditor = null;
  if (isVisualizationTypeParameters(parameters, VisualizationType.SCATTER_PLOT)) {
    const scatterPlotParameters = parameters;
    YAxisMinMaxEditor = (
      <AxisRangeConfigEditor<ScatterPlotParameters, 'yAxisMin', 'yAxisMax'>
        axisLabel="Y-Axis Range"
        minKey="yAxisMin"
        maxKey="yAxisMax"
        parameters={scatterPlotParameters}
        onUpdateParameters={onUpdateParameters}
        warning={
          scatterPlotParameters.yAxisIncludesZero &&
          (scatterPlotParameters.yAxisMax || scatterPlotParameters.yAxisMin)
            ? Y_AXIS_INCLUDES_0_OVERRIDES_RANGE_WARNING
            : null
        }
      />
    );
  } else if (isAnalysisTool && isVisualizationTypeParameters(parameters, VisualizationType.LINE_CHART)) {
    // These settings only exist in analysis tools' version of the parameters
    const lineChartParameters = parameters as LineChartParameters & {
      yAxisMin: number | null;
      yAxisMax: number | null;
    };
    YAxisMinMaxEditor = (
      <AxisRangeConfigEditor<LineChartParameters, 'yAxisMin', 'yAxisMax'>
        axisLabel="Y-Axis Range"
        minKey="yAxisMin"
        maxKey="yAxisMax"
        parameters={lineChartParameters}
        onUpdateParameters={onUpdateParameters}
        warning={
          lineChartParameters.yAxisIncludesZero &&
          (lineChartParameters.yAxisMax || lineChartParameters.yAxisMin)
            ? Y_AXIS_INCLUDES_0_OVERRIDES_RANGE_WARNING
            : null
        }
      />
    );
  }

  let XAxisRangeEditor = null;
  if (isAnalysisTool && isVisualizationTypeParameters(parameters, VisualizationType.SCATTER_PLOT)) {
    // These settings only exists in analysis tools' version of the parameters. i.e. not AnalyticsVisualizationScatterPlotParametersNode,
    // so we do a custom retype.
    const scatterPlotParameters = parameters as ScatterPlotParameters & {
      xAxisMin: number | null;
      xAxisMax: number | null;
    };
    XAxisRangeEditor = (
      <AxisRangeConfigEditor<ScatterPlotParameters, 'xAxisMin', 'xAxisMax'>
        axisLabel="X-Axis Range"
        minKey="xAxisMin"
        maxKey="xAxisMax"
        parameters={scatterPlotParameters}
        onUpdateParameters={onUpdateParameters}
      />
    );
  } else if (isAnalysisTool && isVisualizationTypeParameters(parameters, VisualizationType.LINE_CHART)) {
    // These settings only exist in analysis tools' version of the parameters
    const lineChartParameters = parameters as LineChartParameters & {
      xAxisMin: number | null;
      xAxisMax: number | null;
    };
    XAxisRangeEditor = (
      <AxisRangeConfigEditor<LineChartParameters, 'xAxisMin', 'xAxisMax'>
        axisLabel="X-Axis Range"
        minKey="xAxisMin"
        maxKey="xAxisMax"
        parameters={lineChartParameters}
        onUpdateParameters={onUpdateParameters}
      />
    );
  }

  return (
    <div {...getTestProps({component: 'FormatConfigEditor', element: 'root'})}>
      <div className="u-input-spacing-top">
        <FieldLabel label="X-Axis" />
        <CheckboxInput
          id="x-axis-log-scale"
          label="Log scale"
          className="u-mini-spacing-top"
          onChange={(e) => {
            onUpdateParameters({xAxisScale: e.target.checked ? AxisScale.LOG : AxisScale.LINEAR});
          }}
          checked={parameters.xAxisScale === AxisScale.LOG}
        />
        {XAxisRangeEditor}
      </div>
      <div className="u-input-spacing-top">
        <FieldLabel className="u-small-spacing-top" label="Y-Axis" />
        <CheckboxInput
          id="y-axis-log-scale"
          label="Log scale"
          className="u-mini-spacing-top"
          onChange={(e) => {
            onUpdateParameters({yAxisScale: e.target.checked ? AxisScale.LOG : AxisScale.LINEAR});
          }}
          checked={parameters.yAxisScale === AxisScale.LOG}
        />
        <CheckboxInput
          id="y-axis-includes-0"
          label="Include 0"
          className="u-small-spacing-top"
          onChange={(e) => {
            onUpdateParameters({yAxisIncludesZero: e.target.checked});
          }}
          checked={parameters.yAxisIncludesZero}
        />
        {YAxisMinMaxEditor}
      </div>
    </div>
  );
}
